import {
  AiProfileFragment,
  BasicUserFragment,
  HumanProfileFragment,
  PhoneNumberFragment,
  UserFragment,
  UserProfileType,
} from '@shared/generated/graphql';
import {
  AIProfile,
  BasicUser,
  HumanProfile,
  PhoneNumber,
  toName,
  User,
  UserProfile,
} from '@shared/types/user';
import { UserFeatures } from 'clerk_common/types';
import { isNil } from 'lodash';

export function basicUserFromFragment(
  fragment?: BasicUserFragment | null
): BasicUser | undefined {
  if (isNil(fragment)) return;
  return {
    id: fragment.id,
    email: fragment.email ?? undefined,
    firstName: fragment.firstName ?? undefined,
    lastName: fragment.lastName ?? undefined,
    username: fragment.username ?? undefined,
    roles: fragment.roles,
    excludeFromReporting: fragment.excludeFromReporting ?? false,
    profile: userProfileFromFragment(fragment.profile),
  };
}

export function userFromFragment(fragment: UserFragment): User {
  return {
    id: fragment.id,
    userName: fragment.username ?? undefined,
    name: toName({
      firstName: fragment.firstName,
      lastName: fragment.lastName,
    }),
    email: fragment.email ?? undefined,
    roles: fragment.roles,
    features: fragment.features as UserFeatures,
    excludeFromReporting: fragment.excludeFromReporting ?? false,
    profile: humanProfileFromFragment(fragment.profile),
    userProfile: userProfileFromFragment(fragment.profile),
  };
}

export const humanProfileFromFragment = (
  profile?: HumanProfileFragment | AiProfileFragment | null
): HumanProfile | undefined => {
  if (profile?.__typename !== 'HumanProfile') return undefined;
  return toHumanProfile(profile);
};

const userProfileFromFragment = (
  profile?: HumanProfileFragment | AiProfileFragment | null
): UserProfile | undefined => {
  switch (profile?.__typename) {
    case 'HumanProfile':
      return toHumanProfile(profile);
    case 'AIProfile':
      return toAIProfile(profile);
    default:
      return undefined;
  }
};

const toAIProfile = (
  profile?: AiProfileFragment | null
): AIProfile | undefined => {
  if (!profile) return undefined;
  return {
    type: UserProfileType.AI,
    name: profile.name ?? undefined,
  };
};

const toHumanProfile = (
  profile?: HumanProfileFragment | null
): HumanProfile | undefined => {
  if (!profile) return undefined;
  const phoneNumber = phoneNumberFromFragment(profile.phoneNumber);
  return {
    type: UserProfileType.HUMAN,
    name: profile.name ?? undefined,
    phoneNumber,
    dtmfPin: profile.dtmfPin ?? undefined,
  };
};

const phoneNumberFromFragment = (
  phoneNumber?: PhoneNumberFragment | null
): PhoneNumber | undefined => {
  if (!phoneNumber?.phoneNumber) return undefined;
  return {
    phoneNumber: phoneNumber.phoneNumber,
    extension: phoneNumber.extension ?? undefined,
  };
};
